const PRIVATE_PARAMS = ['ph', 'email', 'coupon', 'doi', 'doi_voucher']
const LINK_TAGS = new Set(['canonical', 'alternate'])

export const NO_INDEX_FOLLOW = 'noindex,follow'
export const INDEX_FOLLOW = 'index,follow'

export const createMetaObject = async ({ title, ...rest }) => {
  const { SiteConfig } = await _getModules()

  const prefix = SiteConfig.devMode ? '[ 👾] ' : ''
  const titleTemplate = `${prefix}%s | ${SiteConfig.name}`
  const pagePrivate = containsPrivateInfo()

  const { meta, link } = mapMetaData(rest, pagePrivate)

  return { titleTemplate, title, meta, link }
}

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [{ default: SiteConfig }, { default: router }] = await Promise.all([
      import('@/services/SiteConfig'),
      import('@/router')
    ])
    cache = {
      SiteConfig,
      router
    }
    return cache
  }
})()

export const containsPrivateInfo = () => {
  // Private params reach the server too: api-v2 redirects a clicked
  // newsletter link back to the storefront with `?doi=…`, a cold navigation
  // that is server-rendered. Read the router's query there, since `location`
  // does not exist (VIP-12037).
  if (import.meta.env.SSR) {
    const query = _requestQueryResolver?.() ?? {}
    return PRIVATE_PARAMS.some((p) => p in query)
  }
  const query = new URLSearchParams(location.search)
  return PRIVATE_PARAMS.some((p) => query.has(p))
}

let _requestQueryResolver = null

/**
 * Installed by the server entry: returns the rendering request's route query
 * (or undefined outside a request). A resolver rather than a stored router so
 * concurrent renders cannot read each other's URL (VIP-12037).
 *
 * @param {() => Record<string, unknown> | undefined} resolver
 */
export const setRequestQueryResolver = (resolver) => {
  _requestQueryResolver = resolver
}

const mapMetaData = (data, noIndex) => {
  // [ { rel: 'something', random_prop: '123' } ]
  const link = Object.keys(data)
    .filter((key) => LINK_TAGS.has(key))
    .reduce(
      (tags, key) => [
        ...tags,
        ...data[key].map((content) => ({ rel: key, ...content }))
      ],
      []
    )

  // [ { vmid: '', name: '', content: '' } ]
  const meta = Object.keys(data)
    .filter((key) => !LINK_TAGS.has(key))
    .reduce((tags, key) => {
      if (!data[key]) {
        return tags
      }
      if (key === 'robots' && noIndex) {
        return [...tags, { vmid: key, name: key, content: NO_INDEX_FOLLOW }]
      }
      return [...tags, { vmid: key, name: key, content: data[key] }]
    }, [])

  return { meta, link }
}
